"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, X } from "lucide-react";
import { buildBlogHref, type BlogQuery } from "@/lib/blog-query";

interface Props {
  query: BlogQuery;
}

/** Real <form method="get"> so it works with zero JS. When JS is available,
 *  typing debounce-navigates for an instant-feeling search without a full
 *  page reload — Enter/submit still works immediately either way. */
export default function BlogSearchBar({ query }: Props) {
  const router = useRouter();
  const [value, setValue] = useState(query.q ?? "");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Resync when the URL's `q` changes from outside this input (e.g. the
  // "Clear filters" link or browser back/forward) — adjusted during render
  // per React's guidance, not in an effect, so it can't cascade renders.
  const [prevQ, setPrevQ] = useState(query.q ?? "");
  if ((query.q ?? "") !== prevQ) {
    setPrevQ(query.q ?? "");
    setValue(query.q ?? "");
  }

  const navigate = (q: string) => {
    router.replace(buildBlogHref(query, { q: q || undefined, page: undefined }), { scroll: false });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => navigate(next), 350);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearTimeout(timeoutRef.current);
    navigate(value);
  };

  const handleClear = () => {
    clearTimeout(timeoutRef.current);
    setValue("");
    navigate("");
  };

  return (
    <form
      role="search"
      method="get"
      action="/blogs"
      onSubmit={handleSubmit}
      className="relative w-full sm:max-w-xs"
    >
      <label htmlFor="blog-search" className="sr-only">
        Search blogs
      </label>
      <Search
        size={16}
        aria-hidden
        className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-muted"
      />
      <input
        id="blog-search"
        name="q"
        type="search"
        value={value}
        onChange={handleChange}
        placeholder="Search blogs…"
        autoComplete="off"
        className="w-full rounded-lg border border-border-subtle bg-bg-card py-2.5 pl-10 pr-9 text-sm text-text-primary placeholder:text-text-muted transition-colors focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple/40 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-text-muted transition-colors hover:bg-white/10 hover:text-text-primary"
        >
          <X size={13} aria-hidden />
        </button>
      )}
    </form>
  );
}
